/** Client-side column sorting for the supplier-page tables (findings +
 *  journal entries). Pure + exported so the header-click cycle and the
 *  comparator are unit-tested without the DOM. Generic over the column key
 *  so both tables share one implementation with their own accessor. */
export type SortDir = 'asc' | 'desc';

export interface SortState<K extends string = string> {
  key: K;
  dir: SortDir;
}

/** Header-click cycle: a new column starts at `desc`; the active column goes
 *  desc → asc → off (null = fall back to the server's order). */
export function cycleSortState<K extends string>(
  current: SortState<K> | null,
  key: K,
): SortState<K> | null {
  if (!current || current.key !== key) return { key, dir: 'desc' };
  if (current.dir === 'desc') return { key, dir: 'asc' };
  return null;
}

/** Return a sorted COPY of `rows` for the active sort, or an untouched copy
 *  (server order preserved) when `sort` is null. `accessor(row, key)` yields
 *  the comparable value: numbers compare numerically, everything else by
 *  locale-aware string compare. A `null` value (a missing/unavailable cell)
 *  always sorts to the END regardless of direction, so blank rows don't jump
 *  to the top when the sort flips to ascending. `Array.prototype.sort` is
 *  stable, so ties keep their server order. */
export function sortRows<T, K extends string>(
  rows: readonly T[],
  sort: SortState<K> | null,
  accessor: (row: T, key: K) => string | number | null,
): T[] {
  const copy = [...rows];
  if (!sort) return copy;
  const mul = sort.dir === 'asc' ? 1 : -1;
  return copy.sort((a, b) => {
    const av = accessor(a, sort.key);
    const bv = accessor(b, sort.key);
    // Missing cells sink to the bottom in both directions.
    if (av === null || bv === null) {
      if (av === bv) return 0;
      return av === null ? 1 : -1;
    }
    if (typeof av === 'number' && typeof bv === 'number') {
      return (av - bv) * mul;
    }
    return String(av).localeCompare(String(bv), undefined, { numeric: true }) * mul;
  });
}
